import { useState, useCallback, useMemo, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import { saveInvoice, resolveOrderItems as resolveItems, buildBaseInvoicePayload } from '../BasicDetails/invoiceApi'
import { updateDrawerMeta } from '@data/state/action/root'

// Quantity/rate must be > 0. Empty / non-numeric values count as missing; entered 0 or negatives are invalid.
const hasValue = (v) => v !== null && v !== undefined && String(v).trim() !== '' && Number.isFinite(Number(v))
const isPositive = (v) => hasValue(v) && Number(v) > 0
export const isInvalidValue = (v) => hasValue(v) && Number(v) <= 0
const isCompleteItem = (item) => isPositive(item?.serviced_weight) && isPositive(item?.price_per_kg)

/**
 * Owns the editable serviced-weight/price rows for the Serviced Quantity card.
 * Edit mode, dirty-check, and save are independent from the Other Charges card
 * (useOtherCharges) - each card saves only its own slice of the invoice.
 */
const useServicedQuantityItems = (supplierPoNumber, update) => {
	const dispatch = useDispatch()
	const invoiceData = useSelector((state) => state.getIn(['root', 'drawerTypeMeta', 'invoiceData']))
	const order = useSelector((state) => state.getIn(['root', 'drawerTypeMeta', 'order']))

	const resolveOrderItems = useCallback(
		() => resolveItems(invoiceData, order, supplierPoNumber),
		[invoiceData, order, supplierPoNumber]
	)

	// Full list for edit mode: the order's items, with the invoice's saved values taking precedence.
	// Only complete items are saved, so incomplete ones must come back from the order to stay editable.
	const allOrderItems = useMemo(() => {
		const saved = resolveOrderItems()
		const base = resolveItems(null, order, supplierPoNumber)
		const savedById = new Map(saved.map((item) => [item?.item_id, item]))
		const baseIds = new Set(base.map((item) => item?.item_id))
		return [
			...base.map((item) => {
				const savedItem = savedById.get(item?.item_id)
				if (!savedItem) return item
				// Rows with no serviced quantity yet follow the order item's current rate, so a price change
				// on the order shows up here. Otherwise the invoice rate wins, falling back to the order rate.
				const useOrderRate =
					(!isPositive(savedItem?.serviced_weight) || !hasValue(savedItem?.price_per_kg)) && hasValue(item?.price_per_kg)
				return { ...savedItem, price_per_kg: useOrderRate ? item.price_per_kg : savedItem?.price_per_kg ?? null }
			}),
			...saved.filter((item) => !baseIds.has(item?.item_id)),
		]
	}, [resolveOrderItems, order, supplierPoNumber])

	const [isEditMode, setIsEditMode] = useState(false)
	const [isSaving, setIsSaving] = useState(false)
	const [orderItems, setOrderItems] = useState(allOrderItems)

	// Keep local copy in sync when the invoice in the drawer changes (e.g. after a save).
	useEffect(() => {
		setOrderItems(allOrderItems)
	}, [allOrderItems])

	// If invoiceData arrives without order_items, patch it in Redux so all readers see consistent data.
	useEffect(() => {
		if (!invoiceData?.order_items?.length) {
			const resolved = resolveOrderItems()
			if (resolved.length) {
				dispatch(updateDrawerMeta({ invoiceData: { ...invoiceData, order_items: resolved } }))
			}
		}
	}, [resolveOrderItems, invoiceData, dispatch])

	const enterEditMode = useCallback(() => {
		setOrderItems(allOrderItems.map((item) => ({ ...item })))
		setIsEditMode(true)
	}, [allOrderItems])

	const setItemField = useCallback((itemId, key, value) => {
		setOrderItems((prev) => prev.map((item) => (item?.item_id === itemId ? { ...item, [key]: value } : item)))
	}, [])

	// View mode lists only items with both quantity and rate.
	const viewOrderItems = useMemo(() => allOrderItems.filter(isCompleteItem), [allOrderItems])

	const isDirty = useMemo(
		() =>
			allOrderItems.some((orig) => {
				const edited = orderItems.find((i) => i?.item_id === orig?.item_id)
				if (!edited) return false
				return (
					String(edited.serviced_weight ?? '') !== String(orig.serviced_weight ?? '') ||
					String(edited.price_per_kg ?? '') !== String(orig.price_per_kg ?? '')
				)
			}),
		[orderItems, allOrderItems]
	)

	const hasInvalid = useMemo(
		() => orderItems.some((item) => isInvalidValue(item?.serviced_weight) || isInvalidValue(item?.price_per_kg)),
		[orderItems]
	)

	const handleUpdate = useCallback(async () => {
		if (hasInvalid) return
		setIsSaving(true)

		const payload = {
			...invoiceData,
			...buildBaseInvoicePayload(invoiceData, order, supplierPoNumber),
			gps_link: invoiceData?.gps_link,
			// Incomplete items stay in local state but are not sent.
			order_items: orderItems.filter(isCompleteItem).map((item) => ({
				...item,
				serviced_weight: item?.serviced_weight === '' || item?.serviced_weight === null ? null : Number(item.serviced_weight),
				price_per_kg: item?.price_per_kg === '' || item?.price_per_kg === null ? null : Number(item.price_per_kg),
			})),
		}

		const ok = await saveInvoice({ payload, fallbackInvoice: invoiceData, order, invoiceData, dispatch, supplierPoNumber, update })
		if (ok) setIsEditMode(false)
		setIsSaving(false)
	}, [hasInvalid, orderItems, invoiceData, order, dispatch, supplierPoNumber, update])

	return {
		invoiceData,
		isEditMode,
		isSaving,
		isDirty,
		hasInvalid,
		orderItems,
		viewOrderItems,
		enterEditMode,
		setItemField,
		handleUpdate,
		setIsEditMode,
	}
}

export default useServicedQuantityItems
